import { computed } from 'vue';
import { useCreateFieldGetter } from '@/common/customisation/composables/useCreateFieldGetter';
import { PUI } from '@pleased-npm/fe-customisation';

/** Shared registry + `guest-form` block; override `blockKey` for `guest-form-actions` where needed. */
export const guestFormBlockParams = {
  registryId: PUI.REGISTRY_IDS.GUEST_FORM_PAGE,
  blockKey: PUI.GUEST_FORM_BLOCK_KEYS.GUEST_FORM,
} as const;

/**
 * Composable for guest form title customisation
 */
export const useGuestFormTitle = () => {
  const { getField } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.TITLE,
  });

  const titleText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const titleTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    titleText,
    titleTextColor,
  };
};

/**
 * Inline API error banner in the title row (message from app/API).
 */
export const useGuestFormApiErrorBanner = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.API_ERROR_BANNER,
  });

  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    isVisible,
    backgroundColor,
    borderColor,
    textColor,
  };
};

/**
 * Composable for guest form name input customisation
 */
export const useGuestFormNameInput = () => {
  const { getField } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.NAME_INPUT,
  });

  const placeholder = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const placeholderColor = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);
  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const errorBorderColor = getField(PUI.COMMON_FIELD_KEYS.ERROR_BORDER_COLOR);

  return {
    placeholder,
    textColor,
    placeholderColor,
    backgroundColor,
    borderColor,
    errorBorderColor,
  };
};

/**
 * Composable for guest form phone input customisation
 */
export const useGuestFormPhoneInput = () => {
  const { getField } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.PHONE_INPUT,
  });

  const placeholder = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const placeholderColor = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);
  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const errorBorderColor = getField(PUI.COMMON_FIELD_KEYS.ERROR_BORDER_COLOR);

  return {
    placeholder,
    textColor,
    placeholderColor,
    backgroundColor,
    borderColor,
    errorBorderColor,
  };
};

/**
 * Composable for guest form email input customisation
 */
export const useGuestFormEmailInput = () => {
  const { getField } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.EMAIL_INPUT,
  });

  const placeholder = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const placeholderColor = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);
  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const errorBorderColor = getField(PUI.COMMON_FIELD_KEYS.ERROR_BORDER_COLOR);

  return {
    placeholder,
    textColor,
    placeholderColor,
    backgroundColor,
    borderColor,
    errorBorderColor,
  };
};

/**
 * Composable for guest form description textarea customisation
 */
export const useGuestFormDescriptionInput = () => {
  const { getField } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.DESCRIPTION_INPUT,
  });

  const placeholder = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_TEXT);
  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const placeholderColor = getField(PUI.COMMON_FIELD_KEYS.PLACEHOLDER_COLOR);
  const backgroundColor = getField(PUI.COMMON_FIELD_KEYS.BACKGROUND_COLOR);
  const borderColor = getField(PUI.COMMON_FIELD_KEYS.BORDER_COLOR);
  const errorBorderColor = getField(PUI.COMMON_FIELD_KEYS.ERROR_BORDER_COLOR);

  return {
    placeholder,
    textColor,
    placeholderColor,
    backgroundColor,
    borderColor,
    errorBorderColor,
  };
};

/**
 * Description character counter (e.g. 0 / 500)
 */
export const useGuestFormDescriptionCharCounter = () => {
  const { getField } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.DESCRIPTION_CHAR_COUNTER,
  });

  const textColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);
  const highlightColor = getField(PUI.COMMON_FIELD_KEYS.HIGHLIGHT_COLOR);

  return {
    textColor,
    highlightColor,
  };
};

/**
 * Composable for guest form error message customisation
 */
export const useGuestFormErrorMessage = () => {
  const { getField } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.ERROR_MESSAGE,
  });

  const errorText = getField(PUI.COMMON_FIELD_KEYS.TEXT_CONTENT);
  const errorTextColor = getField(PUI.COMMON_FIELD_KEYS.TEXT_COLOR);

  return {
    errorText,
    errorTextColor,
  };
};

/**
 * Back to login — `guest-form` block (not `guest-form-actions` / not OTP).
 */
export const useGuestFormBackButton = () => {
  const { getField, isVisible } = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.BACK_BUTTON,
  });

  const buttonText = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT);
  const buttonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BACKGROUND_COLOR);
  const buttonBorderColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_BORDER_COLOR);
  const buttonTextColor = getField(PUI.COMMON_FIELD_KEYS.BUTTON_TEXT_COLOR);
  const disabledButtonBackgroundColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BACKGROUND_COLOR);
  const disabledButtonBorderColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_BORDER_COLOR);
  const disabledButtonTextColor = getField(PUI.COMMON_FIELD_KEYS.DISABLED_BUTTON_TEXT_COLOR);

  return {
    isVisible,
    buttonText,
    buttonBackgroundColor,
    buttonBorderColor,
    buttonTextColor,
    disabledButtonBackgroundColor,
    disabledButtonBorderColor,
    disabledButtonTextColor,
  };
};

const isRequired = (value: string, defaultRequired: boolean): boolean =>
  value === 'true' ? true : value === 'false' ? false : defaultRequired;

/**
 * Per-field optional/required flags for the guest form, read from the `required`
 * toggle on each input section. Name/email/description default to required; phone
 * defaults to optional. The field ref is cached outside the computed to avoid
 * calling getField on every reactive evaluation.
 */
export const useGuestFieldRequirements = () => {
  const nameFields = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.NAME_INPUT,
  });
  const emailFields = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.EMAIL_INPUT,
  });
  const descriptionFields = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.DESCRIPTION_INPUT,
  });
  const phoneFields = useCreateFieldGetter({
    ...guestFormBlockParams,
    sectionKey: PUI.GUEST_FORM_SECTION_KEYS.PHONE_INPUT,
  });

  const nameRequired = nameFields.getField(PUI.COMMON_FIELD_KEYS.REQUIRED);
  const emailRequired = emailFields.getField(PUI.COMMON_FIELD_KEYS.REQUIRED);
  const descRequired = descriptionFields.getField(PUI.COMMON_FIELD_KEYS.REQUIRED);
  const phoneRequired = phoneFields.getField(PUI.COMMON_FIELD_KEYS.REQUIRED);

  return {
    nameOptional: computed(() => !isRequired(nameRequired.value, true)),
    emailOptional: computed(() => !isRequired(emailRequired.value, true)),
    descriptionOptional: computed(() => !isRequired(descRequired.value, true)),
    phoneOptional: computed(() => !isRequired(phoneRequired.value, false)),
  };
};

/**
 * Combined composable that returns all guest form customisations
 */
export const useGuestFormAllCustomisation = () => {
  const title = useGuestFormTitle();
  const apiErrorBanner = useGuestFormApiErrorBanner();
  const nameInput = useGuestFormNameInput();
  const phoneInput = useGuestFormPhoneInput();
  const emailInput = useGuestFormEmailInput();
  const descriptionInput = useGuestFormDescriptionInput();
  const descriptionCharCounter = useGuestFormDescriptionCharCounter();
  const errorMessage = useGuestFormErrorMessage();

  return {
    title,
    apiErrorBanner,
    nameInput,
    phoneInput,
    emailInput,
    descriptionInput,
    descriptionCharCounter,
    errorMessage,
  };
};
